import { useMemo, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { ChevronUp, ChevronDown, GripVertical } from 'lucide-react'
import { useLocale } from '../i18n/context'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  useCourse,
  useRemoveDeckFromCourse,
  useRemoveFolderFromCourse,
  useReorderCourseItems,
} from '../hooks/useCourses'
import CourseSettingsModal from '../components/CourseSettingsModal'
import AddToCourseModal from '../components/AddToCourseModal'
import type { CourseDeckSummary, CourseFolderContent } from '../types'
import { libraryHref } from '../utils/libraryAddress'

// Decks and folders share one ordered list within the course. A top-level item
// is either a folder (with its nested decks) or a standalone deck.
type CourseItem =
  | { kind: 'folder'; id: string; position: number; folder: CourseFolderContent }
  | { kind: 'deck'; id: string; position: number; deck: CourseDeckSummary }

const itemKey = (it: CourseItem) => `${it.kind}:${it.id}`

export default function CourseDetailPage() {
  const { tr } = useLocale()
  const cd = tr.courseDetail
  const lib = tr.library
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const fromUrl: string | undefined = (location.state as { from?: string } | null)?.from

  const { data: course, isLoading } = useCourse(id!)
  const removeDeck = useRemoveDeckFromCourse()
  const removeFolder = useRemoveFolderFromCourse()
  const reorder = useReorderCourseItems()

  const [showSettings, setShowSettings] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [confirmRemoveDeck, setConfirmRemoveDeck] = useState<string | null>(null)
  const [confirmRemoveFolder, setConfirmRemoveFolder] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  // Optimistic order applied during a reorder; null = use server order.
  const [localItems, setLocalItems] = useState<CourseItem[] | null>(null)
  const [reorderError, setReorderError] = useState<string | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)

  const sensors = useSensors(
    // Mouse/pen: small drag distance avoids hijacking clicks.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Touch: long-press before drag so vertical scroll is not hijacked.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    // Keyboard/AT alternative — the grip handle is focusable and operable.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // Merge folders + standalone decks into the shared ordered sequence.
  const serverItems = useMemo<CourseItem[]>(() => {
    if (!course) return []
    const merged: CourseItem[] = [
      ...course.folders.map((f) => ({ kind: 'folder' as const, id: f.id, position: f.position, folder: f })),
      ...course.standalone_decks.map((d) => ({ kind: 'deck' as const, id: d.id, position: d.position, deck: d })),
    ]
    // Stable sort by persisted position; ties keep folders-before-decks order.
    return merged.sort((a, b) => a.position - b.position)
  }, [course])

  if (isLoading) return <Skeleton />
  if (!course) return <div style={{ padding: 40, color: 'var(--text-secondary)' }} className="max-md:!px-0">{cd.notFound}</div>

  const items = localItems ?? serverItems
  const totalDecks = course.folders.reduce((sum, f) => sum + f.decks.length, 0) + course.standalone_decks.length
  const canReorder = items.length > 1

  function toggleFolder(folderId: string) {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  function persistOrder(next: CourseItem[]) {
    setLocalItems(next)
    setReorderError(null)
    reorder.mutate(
      { courseId: course!.id, items: next.map((it, i) => ({ type: it.kind, id: it.id, position: i })) },
      {
        onSuccess: () => setLocalItems(null),
        onError: () => {
          setLocalItems(null) // roll back to server order
          setReorderError(cd.reorderError)
        },
      },
    )
  }

  function handleDragStart({ active }: DragStartEvent) {
    setDraggingId(active.id as string)
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setDraggingId(null)
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex((it) => itemKey(it) === active.id)
    const newIndex = items.findIndex((it) => itemKey(it) === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    persistOrder(arrayMove(items, oldIndex, newIndex))
  }

  // Keyboard/button accessible alternative to dragging.
  function handleMove(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= items.length) return
    persistOrder(arrayMove(items, index, newIndex))
  }

  function handleRemoveDeck(deckId: string) {
    setRemoveError(null)
    removeDeck.mutate({ courseId: course!.id, deckId }, {
      onSuccess: () => setConfirmRemoveDeck(null),
      onError: () => {
        setConfirmRemoveDeck(null)
        setRemoveError(cd.removeDeckError)
      },
    })
  }

  function handleRemoveFolder(folderId: string) {
    setRemoveError(null)
    removeFolder.mutate({ courseId: course!.id, folderId }, {
      onSuccess: () => setConfirmRemoveFolder(null),
      onError: () => {
        setConfirmRemoveFolder(null)
        setRemoveError(cd.removeFolderError)
      },
    })
  }

  const draggingItem = draggingId ? items.find((it) => itemKey(it) === draggingId) ?? null : null

  const list = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((it, index) => {
        const moveProps = {
          index,
          total: items.length,
          onMoveUp: () => handleMove(index, 'up'),
          onMoveDown: () => handleMove(index, 'down'),
        }
        if (it.kind === 'folder') {
          return (
            <SortableRow key={itemKey(it)} id={itemKey(it)} disabled={!canReorder}>
              {(handle) => (
                <FolderItem
                  folder={it.folder}
                  isOpen={expandedFolders.has(it.id)}
                  onToggle={() => toggleFolder(it.id)}
                  confirmRemove={confirmRemoveFolder === it.id}
                  onConfirmRemove={() => setConfirmRemoveFolder(it.id)}
                  onCancelRemove={() => setConfirmRemoveFolder(null)}
                  onRemove={() => handleRemoveFolder(it.id)}
                  isRemoving={removeFolder.isPending}
                  confirmRemoveDeck={confirmRemoveDeck}
                  onRequestRemoveDeck={setConfirmRemoveDeck}
                  onCancelRemoveDeck={() => setConfirmRemoveDeck(null)}
                  onRemoveDeck={handleRemoveDeck}
                  isRemovingDeck={removeDeck.isPending}
                  onNavigateDeck={(deckId) => navigate(`/decks/${deckId}`)}
                  dragHandle={handle}
                  canReorder={canReorder}
                  {...moveProps}
                />
              )}
            </SortableRow>
          )
        }
        return (
          <SortableRow key={itemKey(it)} id={itemKey(it)} disabled={!canReorder}>
            {(handle) => (
              <DeckRow
                title={it.deck.title}
                cardCount={it.deck.card_count}
                confirmRemove={confirmRemoveDeck === it.id}
                onNavigate={() => navigate(`/decks/${it.id}`)}
                onRemove={() => handleRemoveDeck(it.id)}
                onConfirmRemove={() => setConfirmRemoveDeck(it.id)}
                onCancelRemove={() => setConfirmRemoveDeck(null)}
                isRemoving={removeDeck.isPending}
                dragHandle={handle}
                canReorder={canReorder}
                {...moveProps}
              />
            )}
          </SortableRow>
        )
      })}
    </div>
  )

  return (
    <div style={{ padding: 40, maxWidth: 900, margin: '0 auto' }} className="max-md:!px-0">
      {/* Back */}
      <button onClick={() => navigate(fromUrl ?? OWN_COURSE_BACK_DEST)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, padding: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
        {courseBackLabel(fromUrl, cd.backMyCourses, cd.backCatalog)}
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{course.title}</h1>
          <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{lib.deckCount(totalDecks)}</span>
            {course.folders.length > 0 && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{lib.folderCount(course.folders.length)}</span>}
            {course.is_public && <span style={{ fontSize: 12, padding: '2px 8px', background: 'var(--accent-soft)', color: 'var(--accent)', borderRadius: 'var(--radius-pill)', fontWeight: 500 }}>{cd.publicBadge}</span>}
          </div>
          {course.description && <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text-secondary)', maxWidth: 560 }}>{course.description}</p>}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => navigate(`/courses/${course.id}/learn`, { state: { from: `/courses/${course.id}` } })} style={{ padding: '10px 20px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {cd.learnAll}
          </button>
          <button onClick={() => setShowSettings(true)} style={{ padding: '10px 18px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, cursor: 'pointer' }}>
            {cd.settingsBtn}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ paddingTop: 20, borderTop: '1px solid var(--border)', marginBottom: 28 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: (removeError || reorderError) ? 12 : 0 }}>
          <button onClick={() => setShowAdd(true)} style={{ padding: '8px 16px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 500, cursor: 'pointer', minHeight: 38 }}>
            {cd.addDeckFolderBtn}
          </button>
        </div>
        {removeError && <ErrorBanner message={removeError} onClose={() => setRemoveError(null)} />}
        {reorderError && <ErrorBanner message={reorderError} onClose={() => setReorderError(null)} />}
      </div>

      {/* Empty */}
      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <p style={{ margin: '0 0 16px', fontSize: 15 }}>{cd.emptyMsg}</p>
          <button onClick={() => setShowAdd(true)} style={{ padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{cd.addContentBtn}</button>
        </div>
      )}

      {/* Mixed ordered list of folders + standalone decks */}
      {items.length > 0 && (
        canReorder ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map(itemKey)} strategy={verticalListSortingStrategy}>
              {list}
            </SortableContext>
            <DragOverlay dropAnimation={{ duration: 150, easing: 'ease' }}>
              {draggingItem ? (
                <DragGhost title={draggingItem.kind === 'folder' ? draggingItem.folder.title : draggingItem.deck.title} isFolder={draggingItem.kind === 'folder'} />
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          list
        )
      )}

      {/* Modals */}
      {showSettings && <CourseSettingsModal course={course} onClose={() => setShowSettings(false)} onDeleted={() => navigate(fromUrl ?? OWN_COURSE_BACK_DEST)} />}
      {showAdd && <AddToCourseModal courseId={course.id} onClose={() => setShowAdd(false)} />}
    </div>
  )
}

// ── Drag handle props passed from SortableRow into each row ──────────────────────

interface DragHandle {
  listeners?: React.HTMLAttributes<HTMLElement>
  attributes?: React.HTMLAttributes<HTMLElement>
}

interface MoveProps {
  index: number
  total: number
  onMoveUp: () => void
  onMoveDown: () => void
}

// ── Sortable wrapper (mirrors SortableCardRow on DeckDetailPage) ─────────────────

function SortableRow({ id, disabled, children }: { id: string; disabled?: boolean; children: (handle: DragHandle) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        position: 'relative',
      }}
    >
      {children({ listeners, attributes: attributes as React.HTMLAttributes<HTMLElement> })}
    </div>
  )
}

// ── Drag handle + reorder buttons (shared by folder + deck rows) ─────────────────

function ReorderControls({ canReorder, dragHandle, index, total, onMoveUp, onMoveDown }: { canReorder: boolean; dragHandle: DragHandle } & MoveProps) {
  const { tr } = useLocale()
  const cd = tr.courseDetail
  if (!canReorder) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      {/* Up/down buttons — keyboard/AT-accessible alternative to dragging */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <button onClick={onMoveUp} disabled={index === 0} aria-label={cd.moveUp} style={{ background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? 'var(--border)' : 'var(--text-secondary)', padding: 0, display: 'flex', lineHeight: 1 }}><ChevronUp size={14} /></button>
        <button onClick={onMoveDown} disabled={index === total - 1} aria-label={cd.moveDown} style={{ background: 'none', border: 'none', cursor: index === total - 1 ? 'default' : 'pointer', color: index === total - 1 ? 'var(--border)' : 'var(--text-secondary)', padding: 0, display: 'flex', lineHeight: 1 }}><ChevronDown size={14} /></button>
      </div>
      {/* Grip handle — explicit drag target; long-press on touch. touchAction:none required. */}
      <div
        {...dragHandle.listeners}
        {...dragHandle.attributes}
        data-drag-handle="true"
        title={cd.dragToReorder}
        aria-label={cd.dragToReorder}
        style={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: 'var(--text-secondary)', opacity: 0.5, touchAction: 'none', padding: '0 2px' }}
      >
        <GripVertical size={16} />
      </div>
    </div>
  )
}

// ── Folder item (collapsible, with nested decks) ─────────────────────────────────

interface FolderItemProps extends MoveProps {
  folder: CourseFolderContent
  isOpen: boolean
  onToggle: () => void
  confirmRemove: boolean
  onConfirmRemove: () => void
  onCancelRemove: () => void
  onRemove: () => void
  isRemoving: boolean
  confirmRemoveDeck: string | null
  onRequestRemoveDeck: (deckId: string) => void
  onCancelRemoveDeck: () => void
  onRemoveDeck: (deckId: string) => void
  isRemovingDeck: boolean
  onNavigateDeck: (deckId: string) => void
  dragHandle: DragHandle
  canReorder: boolean
}

function FolderItem({
  folder, isOpen, onToggle, confirmRemove, onConfirmRemove, onCancelRemove, onRemove, isRemoving,
  confirmRemoveDeck, onRequestRemoveDeck, onCancelRemoveDeck, onRemoveDeck, isRemovingDeck, onNavigateDeck,
  dragHandle, canReorder, index, total, onMoveUp, onMoveDown,
}: FolderItemProps) {
  const { tr } = useLocale()
  const cd = tr.courseDetail
  const lib = tr.library
  return (
    <div>
      {/* Folder header */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--bg-surface)', borderRadius: isOpen ? '12px 12px 0 0' : 'var(--radius-sm)', border: '1px solid var(--border)', cursor: 'pointer' }}
        onClick={onToggle}
      >
        <ReorderControls canReorder={canReorder} dragHandle={dragHandle} index={index} total={total} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
        <span style={{ fontSize: 14, transition: 'transform 0.15s', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        <span style={{ fontSize: 16 }}>📁</span>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{folder.title}</span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{lib.deckCount(folder.decks.length)}</span>
        {confirmRemove ? (
          <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <button onClick={onRemove} disabled={isRemoving}
              style={{ padding: '4px 10px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 11, cursor: 'pointer' }}>{cd.confirmRemove}</button>
            <button onClick={onCancelRemove}
              style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 11, cursor: 'pointer', color: 'var(--text-secondary)' }}>{cd.cancelRemove}</button>
          </div>
        ) : (
          <button onClick={(e) => { e.stopPropagation(); onConfirmRemove() }}
            style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 11, cursor: 'pointer', color: 'var(--danger)' }}>
            {cd.removeFolder}
          </button>
        )}
      </div>

      {/* Folder decks (nested, not independently draggable) */}
      {isOpen && (
        <div style={{ border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
          {folder.decks.length === 0 ? (
            <div style={{ padding: '12px 16px 12px 44px', fontSize: 13, color: 'var(--text-secondary)' }}>{cd.noDecksInFolder}</div>
          ) : (
            folder.decks.map((deck, i) => (
              <DeckRow
                key={deck.id}
                title={deck.title}
                cardCount={deck.card_count}
                indent
                isLast={i === folder.decks.length - 1}
                confirmRemove={confirmRemoveDeck === deck.id}
                onNavigate={() => onNavigateDeck(deck.id)}
                onRemove={() => onRemoveDeck(deck.id)}
                onConfirmRemove={() => onRequestRemoveDeck(deck.id)}
                onCancelRemove={onCancelRemoveDeck}
                isRemoving={isRemovingDeck}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ── DeckRow ─────────────────────────────────────────────────────────────────────

interface DeckRowProps {
  title: string
  cardCount: number
  indent?: boolean
  isLast?: boolean
  confirmRemove: boolean
  onNavigate: () => void
  onRemove: () => void
  onConfirmRemove: () => void
  onCancelRemove: () => void
  isRemoving: boolean
  // Reorder controls present only on top-level standalone deck rows.
  dragHandle?: DragHandle
  canReorder?: boolean
  index?: number
  total?: number
  onMoveUp?: () => void
  onMoveDown?: () => void
}

function DeckRow({ title, cardCount, indent, isLast, confirmRemove, onNavigate, onRemove, onConfirmRemove, onCancelRemove, isRemoving, dragHandle, canReorder, index, total, onMoveUp, onMoveDown }: DeckRowProps) {
  const { tr } = useLocale()
  const cd = tr.courseDetail
  const showReorder = canReorder && dragHandle && index !== undefined && total !== undefined && onMoveUp && onMoveDown
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: `12px 16px 12px ${indent ? 44 : 16}px`,
        background: 'var(--bg-secondary)',
        border: indent ? undefined : '1px solid var(--border)',
        borderRadius: indent ? undefined : 'var(--radius-sm)',
        borderBottom: indent && !isLast ? '1px solid var(--border)' : (indent ? 'none' : '1px solid var(--border)'),
        cursor: 'pointer',
      }}
      onClick={onNavigate}
    >
      {showReorder && (
        <ReorderControls canReorder={true} dragHandle={dragHandle!} index={index!} total={total!} onMoveUp={onMoveUp!} onMoveDown={onMoveDown!} />
      )}
      <span style={{ fontSize: 14 }}>🃏</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{title}</span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{cd.cardsCount(cardCount)}</span>
      </div>
      {confirmRemove ? (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button onClick={onRemove} disabled={isRemoving}
            style={{ padding: '4px 10px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 11, cursor: 'pointer' }}>{cd.confirmRemove}</button>
          <button onClick={onCancelRemove}
            style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 11, cursor: 'pointer', color: 'var(--text-secondary)' }}>{cd.cancelRemove}</button>
        </div>
      ) : (
        <button onClick={(e) => { e.stopPropagation(); onConfirmRemove() }}
          style={{ padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 11, cursor: 'pointer', color: 'var(--text-secondary)', flexShrink: 0, transition: 'color 0.15s, border-color 0.15s', minHeight: 30 }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.borderColor = 'var(--danger)' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)' }}
        >
          {cd.confirmRemove}
        </button>
      )}
    </div>
  )
}

// ── Drag ghost shown in the DragOverlay ──────────────────────────────────────────

function DragGhost({ title, isFolder }: { title: string; isFolder: boolean }) {
  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--accent)',
      borderRadius: 'var(--radius-sm)',
      padding: '12px 16px',
      boxShadow: 'var(--shadow-float)',
      display: 'flex', alignItems: 'center', gap: 10,
      cursor: 'grabbing',
    }}>
      <GripVertical size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
      <span style={{ fontSize: 14 }}>{isFolder ? '📁' : '🃏'}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
    </div>
  )
}

// ── Inline error banner (--danger) ───────────────────────────────────────────────

function ErrorBanner({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div style={{ marginTop: 8, padding: '10px 14px', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
    </div>
  )
}

// Where a user's own course returns to when it was opened without a `from`
// (e.g. straight after "Group into course" in Mine). This was `/courses`, which
// redirected to Mine; `/courses` now lands on Catalog › Courses (ADR-106 Q10),
// the system courses, so the reader's own course names Mine directly and keeps
// returning where it always has.
const OWN_COURSE_BACK_DEST = libraryHref('mine')

function courseBackLabel(from: string | undefined, labelMyCourses: string, labelCatalog: string): string {
  if (from) {
    const params = new URLSearchParams(from.split('?')[1] ?? '')
    const seg = params.get('seg')
    if (from.startsWith('/library')) {
      if (seg === 'catalog') return labelCatalog
      return labelMyCourses
    }
    if (from.startsWith('/courses')) return labelMyCourses
    if (from.startsWith('/catalog')) return labelCatalog
  }
  return labelMyCourses
}

function Skeleton() {
  return (
    <div style={{ padding: 40, maxWidth: 900, margin: '0 auto' }} className="max-md:!px-0">
      <div style={{ height: 32, width: 200, background: 'var(--border)', borderRadius: 8, marginBottom: 12 }} />
      <div style={{ height: 16, width: 120, background: 'var(--border)', borderRadius: 4, marginBottom: 32 }} />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} style={{ height: 52, background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 8 }} />
      ))}
    </div>
  )
}
